import { useState, useEffect, useCallback } from "react"
import { Popover, ActionIcon, Indicator, ScrollArea, Group, Text, UnstyledButton, Loader, Tooltip } from "@mantine/core"
import { useNavigate } from "react-router-dom"
import { useTranslation, Trans } from "react-i18next"
import {
  useNotificationsCount,
  useNotificationsList,
  useMarkAsRead,
  useMarkAllAsRead,
} from "../hooks/useNotifications"
import type { Notification } from "../models/notification"
import { parseNotificationUrl, buildDrawerNavigation } from "../../../helpers/drawerQueryParams"

const UNREAD_COUNT_TRUNCATE_AT = 99

const getNotificationIconInfo = (className: string) => {
  const normalizedClass = className.toLowerCase()
  if (normalizedClass.includes("document")) {
    return {
      icon: "fa fa-file-text-o",
      colorClass: "text-blue-500",
      bgClass: "bg-blue-50",
    }
  }
  if (normalizedClass.includes("agencyvisit")) {
    return {
      icon: "fa fa-map-marker",
      colorClass: "text-green-500",
      bgClass: "bg-green-50",
    }
  }
  if (normalizedClass.includes("travelercheck")) {
    return {
      icon: "fa fa-ticket",
      colorClass: "text-teal-500",
      bgClass: "bg-teal-50",
    }
  }
  if (normalizedClass.includes("post")) {
    return {
      icon: "fa fa-comments-o",
      colorClass: "text-orange-500",
      bgClass: "bg-orange-50",
    }
  }
  return {
    icon: "fa fa-bell-o",
    colorClass: "text-gray-500",
    bgClass: "bg-gray-50",
  }
}

export default function NotificationsBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [opened, setOpened] = useState(false)
  const [justUpdated, setJustUpdated] = useState(false)

  const { data: countData } = useNotificationsCount()
  const { data: listData, isLoading } = useNotificationsList(10)

  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()

  const unseenCount = countData?.count ?? 0
  const displayCount =
    unseenCount > UNREAD_COUNT_TRUNCATE_AT
      ? `${UNREAD_COUNT_TRUNCATE_AT}+`
      : String(unseenCount)

  const showUnseenBadge = unseenCount > 0

  useEffect(() => {
    if (!showUnseenBadge) return
    setJustUpdated(true)
    const timer = setTimeout(() => setJustUpdated(false), 600)
    return () => clearTimeout(timer)
  }, [unseenCount])

  const handleItemClick = useCallback(
    (notif: Notification) => {
      if (!notif.read) {
        markAsRead.mutate(notif.id)
      }
      setOpened(false)
      if (notif.url) {
        const drawerNav = buildDrawerNavigation(notif.url)
        if (drawerNav) {
          navigate(drawerNav)
        } else if (notif.url.startsWith("http")) {
          window.open(notif.url, "_blank", "noopener,noreferrer")
        } else {
          navigate(notif.url)
        }
      }
    },
    [markAsRead, navigate],
  )

  const handleMarkAsReadClick = useCallback(
    (e: React.MouseEvent, id: number) => {
      e.stopPropagation()
      markAsRead.mutate(id)
    },
    [markAsRead],
  )

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={380}
      position="bottom-end"
      withArrow
      shadow="lg"
      closeOnClickOutside
      closeOnEscape
      trapFocus
      styles={{
        dropdown: {
          padding: 0,
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      <Popover.Target>
        <button
          type="button"
          aria-label={t("notifications.title")}
          aria-expanded={opened}
          aria-haspopup="true"
          className="relative cursor-pointer bg-transparent border-0 p-0"
          onClick={() => setOpened((o) => !o)}
        >
          <Indicator
            disabled={!showUnseenBadge}
            label={displayCount}
            size={showUnseenBadge && unseenCount > 9 ? 24 : 20}
            color="red"
            offset={4}
            styles={{
              indicator: {
                backgroundColor: "#ef4444",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: unseenCount > 9 ? "10px" : "11px",
                padding: unseenCount > 9 ? "0 4px" : undefined,
                minWidth: unseenCount > 9 ? 20 : undefined,
                lineHeight: unseenCount > 9 ? "20px" : undefined,
                border: "2px solid var(--primary-color, #2563eb)",
                boxSizing: "border-box",
              },
            }}
          >
            <ActionIcon
              variant="transparent"
              color="white"
              size="lg"
              className={`hover:opacity-80 transition-all ${justUpdated ? "animate-bell-shake" : ""}`}
              style={{ fontSize: "20px", color: "white" }}
            >
              <i className="fa" style={{ fontSize: "20px" }}>
                &#xf0f3;
              </i>
            </ActionIcon>
          </Indicator>
        </button>
      </Popover.Target>

      <Popover.Dropdown p="0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <Text size="sm" fw={600} className="text-gray-800">
            <Trans i18nKey="notifications.title" />
          </Text>
          {showUnseenBadge && (
            <Tooltip
              label={t("notifications.markAllAsReadHelp", {
                count: unseenCount,
              })}
              position="left"
              withArrow
            >
              <UnstyledButton
                onClick={() => markAllAsRead.mutate()}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Trans i18nKey="notifications.markAllAsRead" />
              </UnstyledButton>
            </Tooltip>
          )}
        </div>

        <ScrollArea.Autosize style={{ maxHeight: 340 }}>
          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <Loader size="sm" color="gray" />
            </div>
          ) : !listData || listData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <i className="fa fa-bell-o text-gray-400 text-xl" />
              </div>
              <Text size="sm" className="text-gray-500">
                <Trans i18nKey="notifications.empty" />
              </Text>
            </div>
          ) : (
            <div>
              {listData.map((notif) => {
                const iconInfo = getNotificationIconInfo(notif.class)
                return (
                  <UnstyledButton
                    key={notif.id}
                    onClick={() => handleItemClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors text-left w-full last:border-b-0 ${
                      !notif.read ? "bg-blue-50/30" : ""
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 ${iconInfo.bgClass}`}
                    >
                      <i
                        className={`${iconInfo.icon} ${iconInfo.colorClass} text-sm`}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <Text
                        size="xs"
                        className={`text-gray-700 break-words leading-relaxed ${!notif.read ? "font-semibold text-gray-900" : ""}`}
                      >
                        {notif.message}
                      </Text>
                      <Text size="10px" className="text-gray-400 mt-1">
                        {notif.timeago}
                      </Text>
                    </div>

                    {!notif.read && (
                      <Tooltip
                        label={t("notifications.markAsRead")}
                        position="left"
                        withArrow
                      >
                        <UnstyledButton
                          onClick={(e) => handleMarkAsReadClick(e, notif.id)}
                          className="flex-shrink-0 self-center w-7 h-7 flex items-center justify-center rounded-full hover:bg-green-50 transition-colors"
                          aria-label={t("notifications.markAsRead")}
                        >
                          <i className="fa fa-circle text-[8px] text-blue-500" />
                        </UnstyledButton>
                      </Tooltip>
                    )}
                  </UnstyledButton>
                )
              })}
            </div>
          )}
        </ScrollArea.Autosize>

        <div className="border-t border-gray-100 text-center py-2.5 bg-gray-50">
          <UnstyledButton
            onClick={() => {
              setOpened(false)
              navigate("/notifications")
            }}
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            <Trans i18nKey="notifications.viewAll" />
          </UnstyledButton>
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}
